import classNames from 'classnames';
import type { TrafficSituation } from '../models/trafficSituation';
import ImageIcon from '@shared/components/ImageIcon';
import {
  CHARACTERS_READ_PER_SECOND,
  MINIMUM_TIME_IN_MILLISECONDS_TO_READ_TEXT,
} from '@shared/constants';
import styles from './TrafficSituation.module.scss';
import { CSSProperties, forwardRef, Ref, useEffect, useRef } from 'react';
import Line from '@shared/components/Line';
import useLinearScroll from '@shared/hooks/useLinearScroll';
import useElementSize from '@shared/hooks/useElementSize';
import useScrollHeight from '@shared/hooks/useScrollHeight';
import ProgressBar from '@shared/components/ProgressBar';
import { useScrollState } from '@shared/hooks/useScrollState';
import { DeepReadonly } from 'ts-essentials';
import { TrafficSituationContext } from '../models/trafficSituationContext';

export type TrafficSituationDisplay =
  | 'expanded'
  | 'collapsed'
  | 'scrollable'
  | 'pagination-measuring';
export const NUMBER_OF_AFFECTED_LINES_CONSIDERED_MANY = 5;

interface TrafficSituationProps {
  trafficSituation: DeepReadonly<TrafficSituation>;
  display: TrafficSituationDisplay;
  context: TrafficSituationContext;
  paginated: boolean;
  paginationId?: string;
  viewTimeUpdated?: (
    trafficSituationId: string,
    newViewTimeInMilliseconds: number,
  ) => void;
  onExpandedViewed?: () => void;
}

const TrafficSituation = forwardRef(
  (
    {
      trafficSituation,
      display,
      context,
      paginated,
      paginationId,
      viewTimeUpdated,
      onExpandedViewed,
    }: TrafficSituationProps,
    trafficSituationRef: Ref<HTMLDivElement>,
  ) => {
    const contentContainerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLHeadingElement>(null);
    const headerSize = useElementSize(headerRef);
    const contentSize = useElementSize(contentContainerRef);
    const contentScrollHeight = useScrollHeight(contentRef);
    const isScrollable =
      display === 'scrollable' && contentScrollHeight && contentSize.height
        ? contentScrollHeight > contentSize.height + 1
        : false;
    const millisecondsToReadContent = Math.max(
      MINIMUM_TIME_IN_MILLISECONDS_TO_READ_TEXT,
      ((trafficSituation.title
        ? trafficSituation.title.length / CHARACTERS_READ_PER_SECOND
        : 0) +
        (trafficSituation.description
          ? trafficSituation.description.length / CHARACTERS_READ_PER_SECOND
          : 0)) *
        1000,
    );
    const hasManyAffectedLines =
      trafficSituation.affectedLines.length >=
      NUMBER_OF_AFFECTED_LINES_CONSIDERED_MANY;
    const showAffectedLines = context === 'stop';

    const { hasMoreContent, hasPreviousContent } = useScrollState(contentRef);

    useLinearScroll(
      contentRef,
      isScrollable ? millisecondsToReadContent : undefined,
      MINIMUM_TIME_IN_MILLISECONDS_TO_READ_TEXT,
    );

    useEffect(() => {
      if (viewTimeUpdated) {
        viewTimeUpdated(
          trafficSituation.id,
          millisecondsToReadContent +
            (isScrollable ? MINIMUM_TIME_IN_MILLISECONDS_TO_READ_TEXT * 2 : 0),
        ); // Delay at start and end when scrolling
      }
    }, [
      isScrollable,
      millisecondsToReadContent,
      trafficSituation.id,
      viewTimeUpdated,
    ]);

    function getIconAltText(): string {
      switch (trafficSituation.severity) {
        case 'slight':
          return 'Låg trafikpåverkan';

        case 'normal':
          return 'Medelstor trafikpåverkan';

        case 'severe':
          return 'Stor trafikpåverkan';
      }
    }

    return (
      <div
        ref={trafficSituationRef}
        data-pagination-id={paginationId}
        className={classNames(
          styles['traffic-situation'],
          styles[`traffic-situation--${context}-context`],
          paginated && styles['traffic-situation--paginated'],
          display === 'collapsed' && styles['traffic-situation--collapsed'],
          display === 'expanded' && styles['traffic-situation--expanded'],
          display === 'pagination-measuring' &&
            styles['traffic-situation--pagination-measuring'],
          trafficSituation.severity === 'normal' &&
            styles['traffic-situation--normal'],
          trafficSituation.severity === 'severe' &&
            styles['traffic-situation--severe'],
        )}
        style={
          {
            '--header-height': `${headerSize.height}px`,
            '--content-height': `${contentScrollHeight}px`,
          } as CSSProperties
        }
      >
        <div
          ref={contentContainerRef}
          className={classNames(
            styles['content-container'],
            isScrollable &&
              hasPreviousContent &&
              styles['content-container--has-previous'],
            isScrollable &&
              hasMoreContent &&
              styles['content-container--has-more'],
          )}
        >
          <div ref={contentRef} className={styles.content}>
            <header ref={headerRef} className={styles.header}>
              <h2
                className={classNames(
                  styles.heading,
                  styles[`heading--${context}-context`],
                )}
              >
                {!hasManyAffectedLines && (
                  <ImageIcon
                    type={`traffic-situation-${trafficSituation.severity}`}
                    alt={getIconAltText()}
                    className={classNames(
                      styles.icon,
                      styles[`icon--${context}-context`],
                    )}
                    themeOverride="light"
                  />
                )}
                {trafficSituation.affectedLines.length > 0 &&
                  showAffectedLines && (
                    <div
                      className={classNames(
                        styles.lines,
                        hasManyAffectedLines && styles['lines--many'],
                      )}
                    >
                      {trafficSituation.affectedLines.map((line) => (
                        <Line key={line.shortName} line={line} size="small" />
                      ))}
                    </div>
                  )}
                {hasManyAffectedLines && (
                  <ImageIcon
                    type={`traffic-situation-${trafficSituation.severity}`}
                    alt={getIconAltText()}
                    className={classNames(
                      styles.icon,
                      styles[`icon--${context}-context`],
                    )}
                    themeOverride="light"
                  />
                )}
                {trafficSituation.title && (
                  <span className={styles.title}>{trafficSituation.title}</span>
                )}
              </h2>
            </header>
            {trafficSituation.description && (
              <p
                className={classNames(
                  styles.description,
                  styles[`description--${context}-context`],
                )}
              >
                {trafficSituation.description}
              </p>
            )}
            {display === 'expanded' && (
              <div className={styles['progress']}>
                <ProgressBar
                  time={millisecondsToReadContent}
                  onDone={onExpandedViewed}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

TrafficSituation.displayName = 'TrafficSituation';

export default TrafficSituation;
